import React, { useEffect, useState, useContext } from 'react';
import {
    Box, Button, Container, TextField, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Card, CardContent, CardMedia,
    Grid, Chip, useTheme, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { Context } from '..';
import { observer } from 'mobx-react-lite';
import BreadCrumbs from "../components/Breadcrumbs";
import {walletHistory, createPayment, getPaymentMethods} from "../http/ShopAPI";
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import {getApiErrorMessage} from "../http/apiError";



const WalletPage = observer(() => {
    const { user } = useContext(Context);
    const theme = useTheme();
    const [amount, setAmount] = useState('');
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [history, setHistory] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [tonConnectUI] = useTonConnectUI();
    const wallet = useTonWallet();

    const handleTopUp = async () => {
        if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
            alert('Введите корректную сумму');
            return;
        }

        if (!selectedMethod) {
            alert('Выберите способ оплаты');
            return;
        }

        try {
            // 🔹 TON
            if (selectedMethod.code === 'TON') {

                // 🔐 Кошелёк не подключён
                if (!wallet) {
                    await tonConnectUI.openModal();
                    return;
                }

                const response = await balance(
                    parseFloat(amount),
                    selectedMethod.id
                );

                await tonConnectUI.sendTransaction({
                    validUntil: Math.floor(Date.now() / 1000) + 600,
                    messages: [{
                        address: response.ton_address,
                        amount: response.amount_nanotons.toString()
                    }]
                });

                setModalMessage(
                    '⏳ Платёж отправлен\nОжидаем подтверждение сети TON'
                );
                setOpenModal(true);
                return;
            } else if (selectedMethod.id === 4) {

                const response = await createPayment(
                    parseFloat(amount),
                    selectedMethod.id
                );
                if (response.url) {
                    // Парсим строку с параметрами в объект
                    const params = JSON.parse(response.request);

                    // Создаём форму
                    const form = document.createElement('form');
                    form.method = 'POST';
                    form.action = response.url;

                    // Добавляем все параметры как скрытые инпуты
                    Object.keys(params).forEach(key => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = key;
                        input.value = params[key];
                        form.appendChild(input);
                    });

                    // Добавляем форму в тело документа и отправляем
                    document.body.appendChild(form);
                    form.submit();
                }
            } else {

                const response = await createPayment(
                    parseFloat(amount),
                    selectedMethod.id
                );

                // 🔹 Обычные методы
                if (response.payment_url) {
                    window.location.href = response.payment_url;
                }
            }

        } catch (err) {
            console.error(err);
            setModalMessage('Ошибка при создании платежа');
            setOpenModal(true);
        }
    };

    // Проверка, подходит ли метод оплаты по сумме
    const isMethodAvailable = (method) => {
        if (!amount || isNaN(amount)) return false;
        const amountValue = parseFloat(amount);
        return amountValue >= method.min_amount && amountValue <= method.max_amount;
    };

    // Проверка, можно ли выбрать метод (введена сумма и метод доступен)
    const isMethodSelectable = (method) => {
        return amount && !isNaN(amount) && isMethodAvailable(method);
    };

    // Функция для форматирования описания с сохранением переносов строк
    const formatDescription = (description) => {
        if (!description) return null;

        // Разбиваем текст на строки по \n
        const lines = description.split('\n');

        return lines.map((line, index) => (
            <React.Fragment key={index}>
                {line}
                {index < lines.length - 1 && <br />}
            </React.Fragment>
        ));
    };

    useEffect(() => {
        walletHistory().then(setHistory);

        // Загрузка методов оплаты
        getPaymentMethods()
            .then(methods => {
                setPaymentMethods(methods || []);
            })
            .catch(err => {
                console.error(getApiErrorMessage(err, 'Ошибка загрузки методов оплаты'));
            });
    }, []);

    const handleCloseModal = () => {
        setOpenModal(false);
        setModalMessage('');
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Box sx={{ width: '100%', marginTop: 3 }}>
                <BreadCrumbs hrefs="" name="Пополнение баланса" />
            </Box>
            <Typography variant="h4" gutterBottom mb={2}>
                Пополнение баланса
            </Typography>

            <Box display="flex" gap={2} mb={4} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                    label="Сумма"
                    type="number"
                    value={amount}
                    onChange={(e) => {
                        setAmount(e.target.value);
                        // Сбрасываем выбор метода при изменении суммы
                        setSelectedMethod(null);
                    }}
                    inputProps={{ min: "1", step: "0.01" }}
                    sx={{ width: { xs: '100%', sm: 200 } }}
                />
                <Button
                    variant="contained"
                    onClick={handleTopUp}
                    disabled={!amount || isNaN(amount) || parseFloat(amount) <= 0 || !selectedMethod}
                >
                    Пополнить
                </Button>
            </Box>

            {/* Отображение всех методов оплаты */}
            {paymentMethods && paymentMethods.length > 0 && (
                <Box mb={4}>
                    <Typography variant="h6" gutterBottom>
                        Способы оплаты:
                    </Typography>
                    <Grid container spacing={2}>
                        {paymentMethods.map((method, index) => {
                            const isAvailable = isMethodAvailable(method);
                            const isSelectable = isMethodSelectable(method);
                            const isSelected = selectedMethod?.id === method.id;

                            return (
                                <Grid item xs={12} sm={6} md={4} key={method.id || index}>
                                    <Card
                                        variant="outlined"
                                        onClick={() => isSelectable && setSelectedMethod(method)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            p: 1,
                                            cursor: isSelectable ? 'pointer' : 'not-allowed',
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? (isSelected ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.05)')
                                                : (isSelected ? theme.palette.primary.light : '#f5f5f5'),
                                            border: isSelected
                                                ? `2px solid ${theme.palette.primary.main}`
                                                : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                            opacity: amount && !isNaN(amount) ? (isAvailable ? 1 : 0.5) : 0.7,
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: isSelectable
                                                    ? (theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.1)'
                                                        : '#e0e0e0')
                                                    : 'inherit',
                                                transform: isSelectable ? 'translateY(-2px)' : 'none',
                                                boxShadow: isSelectable ? '0 4px 8px rgba(0,0,0,0.2)' : 'none'
                                            },
                                            height: '100%',
                                            minHeight: 120
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                                            {method.image_url && (
                                                <CardMedia
                                                    component="img"
                                                    sx={{
                                                        width: 60,
                                                        height: 40,
                                                        objectFit: 'contain',
                                                        mr: 2,
                                                        filter: theme.palette.mode === 'dark' ? 'brightness(1.2)' : 'none'
                                                    }}
                                                    image={method.image_url}
                                                    alt={method.name}
                                                />
                                            )}
                                            <CardContent sx={{
                                                flex: 1,
                                                p: 0,
                                                '&:last-child': { pb: 0 },
                                                color: theme.palette.mode === 'dark'
                                                    ? (isSelected ? 'white' : 'inherit')
                                                    : (isSelected ? 'white' : 'inherit')
                                            }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    component="div"
                                                    sx={{
                                                        fontWeight: isSelected ? 'bold' : 'normal',
                                                        color: isSelected ? 'white' : 'inherit',
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {method.name}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: theme.palette.mode === 'dark'
                                                            ? (isSelected ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary')
                                                            : (isSelected ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary')
                                                    }}
                                                >
                                                    {method.min_amount}₽ - {method.max_amount}₽
                                                </Typography>
                                            </CardContent>
                                        </Box>

                                        {method.description && (
                                            <CardContent sx={{
                                                p: 0,
                                                pt: 1,
                                                flex: 1,
                                                '&:last-child': { pb: 0 },
                                                color: theme.palette.mode === 'dark'
                                                    ? (isSelected ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary')
                                                    : (isSelected ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary')
                                            }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        lineHeight: 1.4,
                                                        whiteSpace: 'pre-line',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {formatDescription(method.description)}
                                                </Typography>
                                            </CardContent>
                                        )}
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {/* Подсказка если сумма не введена */}
                    {!amount && (
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mt: 1,
                                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
                            }}
                        >
                            Введите сумму для выбора способа оплаты
                        </Typography>
                    )}
                </Box>
            )}

            <Typography variant="h5" gutterBottom>
                История транзакций
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Дата</TableCell>
                            <TableCell>Тип</TableCell>
                            <TableCell>Сумма</TableCell>
                            <TableCell>Статус</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {history && history.length > 0 ? (
                            history.map((item, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{item.created_at ? new Date(item.created_at).toLocaleString() : '—'}</TableCell>
                                    <TableCell>{item.type || '—'}</TableCell>
                                    <TableCell>{item.amount ? `${item.amount}₽` : '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={item.status || '—'}
                                            color={
                                                item.status === 'completed' ? 'success' :
                                                    item.status === 'pending' ? 'warning' :
                                                        item.status === 'failed' ? 'error' : 'default'
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    Нет данных
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {/* Модальное окно для сообщений */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {selectedMethod ? selectedMethod.name : 'Сообщение'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{
                        whiteSpace: 'pre-line',
                        color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.87)'
                    }}>
                        {modalMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} variant="contained">
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
});

export default WalletPage;
